import { useState, useEffect, useRef } from 'react';
import { useT } from '../i18n/useT';
import { contactModal as cmEn } from '../i18n/translations/en';
import { contactModal as cmEs } from '../i18n/translations/es';
import './ContactModal.css';

const EMPTY = { firstName: '', lastName: '', email: '', phone: '', company: '', message: '' };

const ContactModal = ({ isOpen, onClose }) => {
    const t = useT({ en: cmEn, es: cmEs });
    const [form, setForm]     = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState('idle'); // idle | sending | success | error
    const formRef = useRef(null);
    const firstInputRef = useRef(null);

    // Focus trap & keyboard close
    useEffect(() => {
        if (!isOpen) return;
        firstInputRef.current?.focus();
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [isOpen, onClose]);

    // Reset on reopen
    useEffect(() => {
        if (isOpen) { setForm(EMPTY); setErrors({}); setStatus('idle'); }
    }, [isOpen]);

    const validate = () => {
        const e = {};
        if (!form.firstName.trim()) e.firstName = true;
        if (!form.lastName.trim())  e.lastName  = true;
        if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = true;
        if (!form.message.trim())   e.message   = true;
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(f => ({ ...f, [name]: value }));
        if (errors[name]) setErrors(ev => ({ ...ev, [name]: false }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setStatus('sending');

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10000);

        try {
            const res = await fetch('/api/contact', {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify(form),
                signal:  controller.signal,
            });
            clearTimeout(timeout);
            if (!res.ok) throw new Error(`${res.status}`);
            setStatus('success');
        } catch (err) {
            clearTimeout(timeout);
            console.error('[Contact] Error:', err.message);
            setStatus('error');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="cm-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="cm-dialog" role="dialog" aria-modal="true" aria-labelledby="cm-title">
                {/* Header */}
                <div className="cm-header">
                    <div>
                        <h2 id="cm-title" className="cm-title">{t.title}</h2>
                        <p className="cm-subtitle">{t.subtitle}</p>
                    </div>
                    <button className="cm-close" onClick={onClose} aria-label={t.close}>✕</button>
                </div>

                {/* Success state */}
                {status === 'success' ? (
                    <div className="cm-success">
                        <div className="cm-success-icon">✓</div>
                        <h3>{t.successTitle}</h3>
                        <p>{t.successBody}</p>
                        <button className="btn btn-primary" onClick={onClose}>{t.close}</button>
                    </div>
                ) : (
                    <form ref={formRef} onSubmit={handleSubmit} noValidate className="cm-form">
                        <div className="cm-row">
                            <div className={`cm-field ${errors.firstName ? 'cm-field--error' : ''}`}>
                                <label htmlFor="cm-firstName">{t.firstName} *</label>
                                <input
                                    ref={firstInputRef}
                                    id="cm-firstName"
                                    name="firstName"
                                    type="text"
                                    value={form.firstName}
                                    onChange={handleChange}
                                    placeholder={t.firstName}
                                />
                                {errors.firstName && <span className="cm-error-msg">{t.required}</span>}
                            </div>
                            <div className={`cm-field ${errors.lastName ? 'cm-field--error' : ''}`}>
                                <label htmlFor="cm-lastName">{t.lastName} *</label>
                                <input
                                    id="cm-lastName"
                                    name="lastName"
                                    type="text"
                                    value={form.lastName}
                                    onChange={handleChange}
                                    placeholder={t.lastName}
                                />
                                {errors.lastName && <span className="cm-error-msg">{t.required}</span>}
                            </div>
                        </div>

                        <div className="cm-row">
                            <div className={`cm-field ${errors.email ? 'cm-field--error' : ''}`}>
                                <label htmlFor="cm-email">{t.email} *</label>
                                <input
                                    id="cm-email"
                                    name="email"
                                    type="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    placeholder="email@ejemplo.com"
                                />
                                {errors.email && <span className="cm-error-msg">{t.required}</span>}
                            </div>
                            <div className="cm-field">
                                <label htmlFor="cm-phone">{t.phone}</label>
                                <input
                                    id="cm-phone"
                                    name="phone"
                                    type="tel"
                                    value={form.phone}
                                    onChange={handleChange}
                                    placeholder="(000) 000-0000"
                                />
                            </div>
                        </div>

                        <div className="cm-field">
                            <label htmlFor="cm-company">{t.company}</label>
                            <input
                                id="cm-company"
                                name="company"
                                type="text"
                                value={form.company}
                                onChange={handleChange}
                                placeholder={t.companyPlaceholder}
                            />
                        </div>

                        <div className={`cm-field ${errors.message ? 'cm-field--error' : ''}`}>
                            <label htmlFor="cm-message">{t.message} *</label>
                            <textarea
                                id="cm-message"
                                name="message"
                                rows={5}
                                value={form.message}
                                onChange={handleChange}
                                placeholder={t.messagePlaceholder}
                            />
                            {errors.message && <span className="cm-error-msg">{t.required}</span>}
                        </div>

                        {status === 'error' && (
                            <p className="cm-send-error">{t.errorBody}</p>
                        )}

                        <div className="cm-actions">
                            <button
                                type="submit"
                                className="btn btn-primary cm-submit"
                                disabled={status === 'sending'}
                            >
                                {status === 'sending' ? t.sending : t.submit}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ContactModal;
